import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { buildPaymentURL, getTrackingData, storeTrackingData, trackPixelEvent } from '../utils/tracking';

const { FiCheck, FiArrowRight, FiClock, FiStar } = FiIcons;

const OFFER_DEADLINE = new Date('2026-03-31T23:59:00').getTime();

const Pricing = () => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    storeTrackingData();

    const timer = setInterval(() => {
      const now = new Date().getTime();
      const distance = OFFER_DEADLINE - now;
      if (distance < 0) {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        clearInterval(timer);
        return;
      }
      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);
      setTimeLeft({ days, hours, minutes, seconds });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const plans = [
    {
      name: 'Monthly', price: '₹1,499', originalPrice: '₹4,997', savings: '70% OFF',
      baseUrl: 'https://paymentz.aifunnels.app/product/21362/25175', popular: false, value: 1499,
      features: ['Unlimited webinars & attendees', 'AI-powered automation', 'HD video streaming', 'Custom branding', 'Built-in analytics', 'WhatsApp support']
    },
    {
      name: 'Yearly', price: '₹7,997', originalPrice: '₹29,997', savings: '73% OFF',
      baseUrl: 'https://paymentz.aifunnels.app/product/21362/25176', popular: true, value: 7997,
      features: ['Everything in Monthly', 'Priority support', 'Advanced analytics', 'Custom integrations', 'Onboarding call', '12 months of updates']
    },
    {
      name: 'Lifetime', price: '₹9,997', originalPrice: '₹99,997', savings: '90% OFF',
      baseUrl: 'https://paymentz.aifunnels.app/product/21362/25177', popular: false, isLifetime: true, value: 9997,
      features: ['Everything in Yearly', 'Lifetime updates', 'VIP support channel', 'Beta feature access', 'Personal onboarding', 'No recurring fees ever']
    }
  ];

  const handlePlanClick = (plan) => {
    const trackingData = getTrackingData();
    trackPixelEvent('InitiateCheckout', { content_name: plan.name, content_category: 'Pricing Plan', value: plan.value, currency: 'INR', num_items: 1 });
    const paymentUrl = buildPaymentURL(plan.baseUrl, {}, trackingData);
    window.location.href = paymentUrl;
  };

  return (
    <section id="pricing" className="py-12 md:py-20 gradient-bg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center mb-12 md:mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <div className="inline-flex items-center space-x-2 bg-gold/20 border border-gold rounded-full px-4 md:px-6 py-2 md:py-3 mb-4 md:mb-6 bounce-animation">
            <SafeIcon icon={FiClock} className="text-gold" />
            <span className="text-gold font-semibold text-xs md:text-sm">
              Financial Year End — Offer Ends 31st March 2026
            </span>
          </div>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold mb-4 md:mb-6 text-gray-900">
            FY End Exclusive —{' '}
            <span className="gradient-text">Choose your access</span>
          </h2>

          <div className="countdown-shine rounded-2xl p-4 md:p-6 max-w-md mx-auto mb-6 md:mb-8 shadow-lg">
            <p className="text-gray-900 font-bold mb-3 text-sm md:text-base">⏰ Offer ends on 31st March 2026 at 11:59 PM:</p>
            <div className="flex justify-center space-x-3 md:space-x-4 text-xl md:text-2xl font-bold">
              <div className="text-center"><div className="text-primary-blue">{timeLeft.days}</div><div className="text-xs text-gray-700">Days</div></div>
              <div className="text-primary-blue">:</div>
              <div className="text-center"><div className="text-primary-blue">{timeLeft.hours}</div><div className="text-xs text-gray-700">Hours</div></div>
              <div className="text-primary-blue">:</div>
              <div className="text-center"><div className="text-primary-blue">{timeLeft.minutes}</div><div className="text-xs text-gray-700">Min</div></div>
              <div className="text-primary-blue">:</div>
              <div className="text-center"><div className="text-primary-blue">{timeLeft.seconds}</div><div className="text-xs text-gray-700">Sec</div></div>
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-12 md:mb-16">
          {plans.map((plan, index) => (
            <motion.div
              key={index}
              className={`rounded-2xl p-6 md:p-8 relative ${plan.isLifetime ? 'golden-card-shine border-2 border-gold shadow-2xl scale-105' : plan.popular ? 'border-2 border-gold shadow-2xl md:scale-105 bg-white' : 'border border-gray-200 shadow-lg bg-white'}`}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              whileHover={{ y: -5, transition: { duration: 0.2 } }}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <div className="bg-gold text-white px-3 md:px-4 py-1.5 md:py-2 rounded-full text-xs md:text-sm font-bold flex items-center space-x-1">
                    <SafeIcon icon={FiStar} /><span>Most Popular</span>
                  </div>
                </div>
              )}
              <div className="absolute -top-2 -right-2">
                <div className="bg-primary-blue text-white px-2 md:px-3 py-1 rounded-full text-xs font-bold">{plan.savings}</div>
              </div>
              <div className="text-center mb-6 md:mb-8">
                <h3 className="font-serif text-xl md:text-2xl font-bold mb-2 text-gray-900">{plan.name}</h3>
                <div className="mb-4">
                  <span className={`text-3xl md:text-4xl font-bold ${plan.isLifetime ? 'text-gray-900' : 'text-primary-blue'}`}>{plan.price}</span>
                  <div className="text-gray-500 line-through text-base md:text-lg">{plan.originalPrice}</div>
                </div>
              </div>
              <div className="space-y-3 md:space-y-4 mb-6 md:mb-8">
                {plan.features.map((feature, featureIndex) => (
                  <div key={featureIndex} className="flex items-center space-x-3">
                    <div className={`w-5 h-5 ${plan.isLifetime ? 'bg-gold/20' : 'bg-blue-100'} rounded-full flex items-center justify-center flex-shrink-0`}>
                      <SafeIcon icon={FiCheck} className={`${plan.isLifetime ? 'text-gold' : 'text-primary-blue'} text-xs md:text-sm`} />
                    </div>
                    <span className={`text-sm md:text-base ${plan.isLifetime ? 'text-gray-900 font-medium' : 'text-gray-700'}`}>{feature}</span>
                  </div>
                ))}
              </div>
              <motion.button
                onClick={() => handlePlanClick(plan)}
                className={`block w-full text-center font-bold py-3 md:py-4 rounded-xl transition-all text-sm md:text-base ${plan.isLifetime ? 'shake-shine-cta bg-gold hover:bg-gold/90 text-white border-2 border-gold' : plan.popular ? 'shake-shine-cta bg-primary-blue hover:bg-dark-blue text-white border-2 border-gold' : 'bg-gray-100 hover:bg-gray-200 text-primary-blue border border-gray-300 hover-glow'} flex items-center justify-center space-x-2`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <span>Buy {plan.name}</span>
                <SafeIcon icon={FiArrowRight} />
              </motion.button>
            </motion.div>
          ))}
        </div>

        <motion.div
          className="bg-white rounded-2xl p-6 md:p-8 shadow-lg overflow-x-auto"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h3 className="font-serif text-xl md:text-2xl font-bold text-center mb-4 md:mb-6 text-gray-900">
            Compare: FY End Offer vs Normal Pricing
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-left min-w-[500px]">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="py-3 md:py-4 pr-4 font-semibold text-gray-900 text-sm md:text-base">Plan</th>
                  <th className="py-3 md:py-4 px-4 font-semibold text-primary-blue text-sm md:text-base">FY End Offer</th>
                  <th className="py-3 md:py-4 px-4 font-semibold text-gray-500 text-sm md:text-base">Normal Price</th>
                  <th className="py-3 md:py-4 pl-4 font-semibold text-gold text-sm md:text-base">You Save</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-100">
                  <td className="py-3 md:py-4 pr-4 text-gray-900 text-sm md:text-base">Monthly</td>
                  <td className="py-3 md:py-4 px-4 text-primary-blue font-bold text-sm md:text-base">₹1,499</td>
                  <td className="py-3 md:py-4 px-4 text-gray-500 line-through text-sm md:text-base">₹4,997</td>
                  <td className="py-3 md:py-4 pl-4 text-gold font-bold text-sm md:text-base">₹3,498</td>
                </tr>
                <tr className="border-b border-gray-100">
                  <td className="py-3 md:py-4 pr-4 text-gray-900 text-sm md:text-base">Yearly</td>
                  <td className="py-3 md:py-4 px-4 text-primary-blue font-bold text-sm md:text-base">₹7,997</td>
                  <td className="py-3 md:py-4 px-4 text-gray-500 line-through text-sm md:text-base">₹29,997</td>
                  <td className="py-3 md:py-4 pl-4 text-gold font-bold text-sm md:text-base">₹22,000</td>
                </tr>
                <tr>
                  <td className="py-3 md:py-4 pr-4 text-gray-900 text-sm md:text-base">Lifetime</td>
                  <td className="py-3 md:py-4 px-4 text-primary-blue font-bold text-sm md:text-base">₹9,997</td>
                  <td className="py-3 md:py-4 px-4 text-gray-500 line-through text-sm md:text-base">₹99,997</td>
                  <td className="py-3 md:py-4 pl-4 text-gold font-bold text-sm md:text-base">₹90,000</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="text-center mt-4 md:mt-6">
            <p className="text-xs md:text-sm text-gray-600">
              Your FY End prices expire on{' '}
              <span className="text-primary-blue font-semibold">March 31, 2026 at 11:59 PM</span>
            </p>
          </div>
        </motion.div>

        <motion.div
          className="text-center mt-8 md:mt-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <div className="flex flex-wrap justify-center items-center gap-4 md:gap-8 text-xs md:text-sm text-gray-600">
            <div className="flex items-center space-x-2"><SafeIcon icon={FiCheck} className="text-green-500" /><span>7-day money-back guarantee</span></div>
            <div className="flex items-center space-x-2"><SafeIcon icon={FiCheck} className="text-green-500" /><span>Secure checkout</span></div>
            <div className="flex items-center space-x-2"><SafeIcon icon={FiCheck} className="text-green-500" /><span>WhatsApp support</span></div>
            <div className="flex items-center space-x-2"><SafeIcon icon={FiCheck} className="text-green-500" /><span>Trusted by 1000+ coaches</span></div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Pricing;